import React from 'react';
import { trackOutboundLink } from '../../utils/eventTracking';

/**
 * OutboundLink component that tracks clicks on external links
 * 
 * @param {Object} props - Component props
 * @param {string} props.href - The URL to link to
 * @param {string} props.className - CSS class names
 * @param {React.ReactNode} props.children - Child elements
 * @param {Object} props.rest - Additional props to pass to the anchor element
 */
const OutboundLink = ({ href, children, className, ...rest }) => {
  const isExternal = href && (
    href.startsWith('http') || 
    href.startsWith('https') || 
    href.startsWith('//')
  ) && !href.includes(window.location.hostname);

  // Remove framer-motion props to avoid DOM warnings
  const { whileHover, whileTap, ...safeRest } = rest || {};

  const handleClick = (e) => {
    // Only track external links in production
    if (process.env.NODE_ENV === 'production' && isExternal) {
      trackOutboundLink(href);
    }

    // If the link has a target="_blank", don't prevent default
    if (rest.target === '_blank') return;

    // For external links without target="_blank", prevent default and open in new tab
    if (isExternal) {
      e.preventDefault();
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <a 
      href={href}
      className={className}
      onClick={handleClick}
      {...(isExternal ? { rel: 'noopener noreferrer', target: '_blank' } : {})}
      {...safeRest}
    >
      {children}
    </a>
  );
};

export default OutboundLink;
